import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { SubmitHandler } from 'react-hook-form';
import { useSearchCivil3dPropertySets } from '../../../hooks/api/use-civil3d-property-sets.hook';
import {
  Civil3dPropertySetApiModel,
  Civil3dPropertySetRelationship,
  WithCivil3dProperties,
} from '../../../../../types/models/civil3d-property-set.model';
import { useGetAttributesForArcgisFeatureClass } from '../../../hooks/api/use-arcgis-feature-classes.hook';
import { PropertyMappingForm } from './PropertyMappingsForm';
import { ArcgisCivil3dPropertyMappingApiModel } from '../../../../../types/models/arcgis-civil3d-property-mapping.model';
import { PropertyMappingRenderer } from './PropertyMappingRenderer';
import {
  Civil3dPropertyType,
  civil3dPropertyTypeAttributeTypeMapping,
} from '../../../../../types/constants/civil3d-property-type';
import { LoadingIndicator } from '../../LoadingIndicator';
import { Civil3dPropertySet } from '../../../../../types/constants/civil3d-property-set';
import { Civil3dFeatureTypeCategory } from '../../../../../types/constants/civil3d-feature-type';

type Props = {
  settingName: string;
  templateId: number;
  featureClassId: number;
  isMappingDefaults?: boolean;
  propertyMappings: ArcgisCivil3dPropertyMappingApiModel[];
  featureTypeCategoryName: Civil3dFeatureTypeCategory;
  onSave: SubmitHandler<PropertyMappingForm>;
};

export const PropertyMappings = ({
  featureTypeCategoryName,
  settingName,
  templateId,
  featureClassId,
  isMappingDefaults,
  propertyMappings,
  onSave,
}: Props) => {
  const navigate = useNavigate();

  const { response: propertySetResponse, loading: loadingPropertySets } = useSearchCivil3dPropertySets<
    Civil3dPropertySetApiModel & WithCivil3dProperties
  >({
    templateId,
    include: [Civil3dPropertySetRelationship.PROPERTIES],
    featureTypeCategoryName,
    isUserDefined: false,
    isObjectProperties: false,
  });
  const { response: userDefinedPropertySetResponse, loading: loadingUserDefinedPropertySets } =
    useSearchCivil3dPropertySets<Civil3dPropertySetApiModel & WithCivil3dProperties>({
      templateId,
      include: [Civil3dPropertySetRelationship.PROPERTIES],
      featureTypeCategoryName,
      isUserDefined: true,
    });
  const { response: objectPropertySetResponse, loading: loadingObjectPropertySets } = useSearchCivil3dPropertySets<
    Civil3dPropertySetApiModel & WithCivil3dProperties
  >({
    templateId,
    include: [Civil3dPropertySetRelationship.PROPERTIES],
    featureTypeCategoryName,
    isObjectProperties: true,
  });

  const { attributes, loading: loadingFeatureClassAttributes } = useGetAttributesForArcgisFeatureClass({
    featureClassId,
  });

  const objectPropertySets = objectPropertySetResponse?.pages.flatMap((page) => page.result) ?? [];
  const propertySets = propertySetResponse?.pages.flatMap((page) => page.result) ?? [];
  const userDefinedPropertySets = userDefinedPropertySetResponse?.pages.flatMap((page) => page.result) ?? [];

  const loading =
    loadingObjectPropertySets || loadingPropertySets || loadingUserDefinedPropertySets || loadingFeatureClassAttributes;

  const handleGetAvailableAttributes = (type: Civil3dPropertyType, usedAttributes?: number[]) =>
    attributes
      ?.filter((attribute) => civil3dPropertyTypeAttributeTypeMapping[type].includes(attribute.type))
      .map((attribute) => ({
        ...attribute,
        isDisabled: (usedAttributes ?? []).includes(attribute.id),
      }));

  const handleGetPropertyMappingDefaults = () => {
    if ((propertyMappings ?? []).length > 0 || !isMappingDefaults) {
      return [];
    }

    const objectPropertySetDefaults = objectPropertySets.reduce((propertySetDefaultsAcc, { properties }) => {
      const propertyDefaults = properties.reduce(
        (propertyDefaultsAcc, { name: propertyName, id: civil3dPropertyId, type: propertyType }) => {
          if (propertyName !== 'GeneralName') {
            return propertyDefaultsAcc;
          }
          const availableAttributes = handleGetAvailableAttributes(propertyType);

          const attribute = availableAttributes?.find(({ name }) => name === 'Code');

          if (!attribute) {
            return propertyDefaultsAcc;
          }

          return [...propertyDefaultsAcc, { civil3dPropertyId, attributeId: attribute.id }];
        },
        [],
      );

      return [...propertySetDefaultsAcc, ...propertyDefaults];
    }, []);

    const propertySetDefaults = (userDefinedPropertySetResponse?.pages[0]?.result ?? []).reduce(
      (propertySetDefaultsAcc, { name: propertySetName, properties }) => {
        if (propertySetName !== Civil3dPropertySet.ARCGIS_WORKAROUND) return propertySetDefaultsAcc;

        const propertyDefaults = properties.reduce(
          (propertyDefaultsAcc, { name: propertyName, id: civil3dPropertyId, type: propertyType }) => {
            if (!['Feature', 'DTM_Type'].includes(propertyName)) {
              return propertyDefaultsAcc;
            }

            const availableAttributes = handleGetAvailableAttributes(propertyType);
            const attribute = availableAttributes?.find(({ name }) => name === propertyName);

            if (!attribute) {
              return propertyDefaultsAcc;
            }

            return [...propertyDefaultsAcc, { civil3dPropertyId, attributeId: attribute.id }];
          },
          [],
        );

        return [...propertySetDefaultsAcc, ...propertyDefaults];
      },
      [],
    );

    return [...objectPropertySetDefaults, ...propertySetDefaults];
  };

  const propertyMappingDefaults = handleGetPropertyMappingDefaults();

  const [isSubmitting, setIsSubmitting] = useState(false);

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <Dialog open={true}>
      <DialogTitle>Property Mappings - {settingName}</DialogTitle>
      <DialogContent>
        {(propertyMappingDefaults ?? []).length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Default property mappings are being shown for this setting.
          </Alert>
        )}
        <PropertyMappingRenderer
          propertyMappings={
            (propertyMappings ?? []).length > 0
              ? propertyMappings.map(({ civil3dProperty, attribute }) => ({
                  civil3dPropertyId: civil3dProperty.id,
                  attributeId: attribute.id,
                }))
              : propertyMappingDefaults
          }
          objectPropertySets={objectPropertySets}
          propertySets={propertySets}
          userDefinedPropertySets={userDefinedPropertySets}
          attributes={attributes || []}
          onSubmit={onSave}
          onSubmittingChange={setIsSubmitting}
          onGetAvailableAttributes={handleGetAvailableAttributes}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => navigate('..')} color="primary">
          Cancel
        </Button>
        <LoadingButton
          color="primary"
          type="submit"
          variant="contained"
          loading={isSubmitting}
          form="property-mappings-form"
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
